import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Grid,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  useTheme,
  CircularProgress,
  Alert,
  Button,
  Tabs,
  Tab
} from '@mui/material';
import {
  Schedule,
  Edit,
  CheckCircle,
  Cancel,
  Email,
  Person,
  CalendarToday,
  PriorityHigh,
  Refresh,
  Support
} from '@mui/icons-material';

const PublicTicketsList = () => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [tabValue, setTabValue] = useState(0);

  useEffect(() => {
    fetchTickets();
  }, []);

  const fetchTickets = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await fetch('https://yiyuz8psi0.execute-api.ap-south-1.amazonaws.com/prod/api/admin/support/tickets', {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setTickets(data.tickets || []);
      } else {
        console.error('API Error:', response.status, response.statusText);
        setError('Unable to load tickets at this time.');
      }
    } catch (error) {
      console.error('Error fetching tickets:', error);
      setError('Unable to load tickets at this time.');
    } finally {
      setLoading(false);
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'urgent': return 'error';
      case 'high': return 'warning';
      case 'medium': return 'info';
      case 'low': return 'success';
      default: return 'default';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'open': return 'error';
      case 'in-progress': return 'warning';
      case 'resolved': return 'success';
      case 'closed': return 'default';
      default: return 'default';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'open': return <Schedule />;
      case 'in-progress': return <Edit />;
      case 'resolved': return <CheckCircle />;
      case 'closed': return <Cancel />;
      default: return <Schedule />;
    }
  };

  const getTicketStats = () => {
    const stats = {
      total: tickets.length,
      open: tickets.filter(t => t.status === 'open').length,
      inProgress: tickets.filter(t => t.status === 'in-progress').length,
      resolved: tickets.filter(t => t.status === 'resolved').length,
      urgent: tickets.filter(t => t.priority === 'urgent').length
    };
    return stats;
  };

  const filteredTickets = tickets.filter(ticket => {
    if (tabValue === 0) return ticket.status === 'open';
    if (tabValue === 1) return ticket.status === 'in-progress';
    if (tabValue === 2) return ticket.status === 'resolved';
    if (tabValue === 3) return ticket.status === 'closed';
    return true;
  });

  const stats = getTicketStats();

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      <Box
        sx={{
          textAlign: 'center',
          mb: 6,
          p: 4,
          borderRadius: 3,
          background: isDark
            ? 'linear-gradient(135deg, rgba(255, 215, 0, 0.1) 0%, rgba(255, 165, 0, 0.05) 100%)'
            : 'linear-gradient(135deg, rgba(59, 130, 246, 0.1) 0%, rgba(147, 197, 253, 0.05) 100%)',
          backdropFilter: 'blur(10px)',
          border: `1px solid ${isDark ? 'rgba(255, 215, 0, 0.2)' : 'rgba(59, 130, 246, 0.2)'}`,
        }}
      >
        <Typography
          variant="h2"
          component="h1"
          sx={{
            fontWeight: 700,
            mb: 2,
            background: isDark
              ? 'linear-gradient(45deg, #FFD700, #FFA500)'
              : 'linear-gradient(45deg, #3B82F6, #93C5FD)',
            backgroundClip: 'text',
            WebkitBackgroundClip: 'text',
            color: 'transparent',
          }}
        >
          Public Support Tickets
        </Typography>
        <Typography variant="h6" color="text.secondary" sx={{ maxWidth: 600, mx: 'auto' }}>
          View all support tickets and their current status
        </Typography>
      </Box>

      <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h4" component="h2" sx={{ fontWeight: 600 }}>
          All Support Tickets
        </Typography>
        <Button
          variant="outlined"
          startIcon={<Refresh />}
          onClick={fetchTickets}
          disabled={loading}
          sx={{
            borderColor: isDark ? 'rgba(255, 215, 0, 0.5)' : 'rgba(59, 130, 246, 0.5)',
            color: isDark ? '#FFD700' : '#3B82F6',
          }}
        >
          Refresh
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 4 }}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          {/* Stats Cards */}
          <Grid container spacing={3} sx={{ mb: 4 }}>
            <Grid item xs={12} sm={6} md={2.4}>
              <Card sx={{ background: isDark ? 'rgba(0,0,0,0.3)' : 'rgba(255,255,255,0.7)' }}>
                <CardContent sx={{ textAlign: 'center' }}>
                  <Typography variant="h4" color="primary">{stats.total}</Typography>
                  <Typography variant="body2">Total Tickets</Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={2.4}>
              <Card sx={{ background: isDark ? 'rgba(244,67,54,0.1)' : 'rgba(244,67,54,0.1)' }}>
                <CardContent sx={{ textAlign: 'center' }}>
                  <Typography variant="h4" color="error">{stats.open}</Typography>
                  <Typography variant="body2">Open</Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={2.4}>
              <Card sx={{ background: isDark ? 'rgba(255,152,0,0.1)' : 'rgba(255,152,0,0.1)' }}>
                <CardContent sx={{ textAlign: 'center' }}>
                  <Typography variant="h4" color="warning.main">{stats.inProgress}</Typography>
                  <Typography variant="body2">In Progress</Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={2.4}>
              <Card sx={{ background: isDark ? 'rgba(76,175,80,0.1)' : 'rgba(76,175,80,0.1)' }}>
                <CardContent sx={{ textAlign: 'center' }}>
                  <Typography variant="h4" color="success.main">{stats.resolved}</Typography>
                  <Typography variant="body2">Resolved</Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={2.4}>
              <Card sx={{ background: isDark ? 'rgba(244,67,54,0.2)' : 'rgba(244,67,54,0.2)' }}>
                <CardContent sx={{ textAlign: 'center' }}>
                  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 1 }}>
                    <PriorityHigh color="error" />
                    <Typography variant="h4" color="error">{stats.urgent}</Typography>
                  </Box>
                  <Typography variant="body2">Urgent</Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Tabs */}
          <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
            <Tabs value={tabValue} onChange={(e, newValue) => setTabValue(newValue)}>
              <Tab label={`Open (${stats.open})`} />
              <Tab label={`In Progress (${stats.inProgress})`} />
              <Tab label={`Resolved (${stats.resolved})`} />
              <Tab label="Closed" />
            </Tabs>
          </Box>

          {/* Tickets Table */}
          <Card sx={{ background: isDark ? 'rgba(0,0,0,0.3)' : 'rgba(255,255,255,0.7)' }}>
            <CardContent>
              <TableContainer component={Paper} sx={{ background: 'transparent' }}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Ticket ID</TableCell>
                      <TableCell>Subject</TableCell>
                      <TableCell>Name</TableCell>
                      <TableCell>Priority</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Created</TableCell>
                      <TableCell>Last Updated</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredTickets.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={7} sx={{ textAlign: 'center', py: 4 }}>
                          <Support sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
                          <Typography variant="h6" color="text.secondary">
                            No tickets found for this status
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      filteredTickets.map((ticket) => (
                        <TableRow key={ticket.id} hover>
                          <TableCell>
                            <Typography variant="body2" fontWeight="bold">
                              {ticket.ticketId}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography 
                              variant="body2" 
                              sx={{ 
                                maxWidth: 200, 
                                overflow: 'hidden', 
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap'
                              }}
                              title={ticket.subject}
                            >
                              {ticket.subject}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Person fontSize="small" />
                              {ticket.name}
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={ticket.priority.toUpperCase()}
                              color={getPriorityColor(ticket.priority)}
                              size="small"
                            />
                          </TableCell>
                          <TableCell>
                            <Chip
                              icon={getStatusIcon(ticket.status)}
                              label={ticket.status.replace('-', ' ').toUpperCase()}
                              color={getStatusColor(ticket.status)}
                              size="small"
                            />
                          </TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <CalendarToday fontSize="small" />
                              <Typography variant="body2">
                                {new Date(ticket.createdAt).toLocaleDateString()}
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <CalendarToday fontSize="small" />
                              <Typography variant="body2">
                                {new Date(ticket.updatedAt).toLocaleDateString()}
                              </Typography>
                            </Box>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>

          {/* Help Section */}
          <Card
            sx={{
              mt: 4,
              background: isDark
                ? 'rgba(0, 0, 0, 0.3)'
                : 'rgba(255, 255, 255, 0.7)',
              backdropFilter: 'blur(10px)',
              border: `1px solid ${isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)'}`,
            }}
          >
            <CardContent sx={{ p: 4, textAlign: 'center' }}>
              <Typography variant="h6" component="h3" gutterBottom sx={{ fontWeight: 600 }}>
                Need Support?
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                Create a new support ticket or track your existing ticket status.
              </Typography>
              <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
                <Button
                  variant="contained"
                  startIcon={<Support />}
                  href="/contact"
                  sx={{
                    background: isDark
                      ? 'linear-gradient(45deg, #FFD700, #FFA500)'
                      : 'linear-gradient(45deg, #3B82F6, #1E40AF)',
                    color: isDark ? '#000' : '#fff',
                    fontWeight: 600,
                  }}
                >
                  Create New Ticket
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<Email />}
                  href="/support/track"
                  sx={{
                    borderColor: isDark ? 'rgba(255, 215, 0, 0.5)' : 'rgba(59, 130, 246, 0.5)',
                    color: isDark ? '#FFD700' : '#3B82F6',
                  }}
                >
                  Track Your Ticket
                </Button>
              </Box>
            </CardContent>
          </Card>
        </>
      )}
    </Container>
  );
};

export default PublicTicketsList;